/**
 * PortfolioComparisonView - Side-by-side portfolio comparison
 * TASK-206 Phase 2: Enhanced Analytics
 *
 * Features:
 * - Multi-select portfolio selector
 * - Side-by-side allocation comparison
 * - Position overlaps detection
 * - Performance metrics comparison
 * - Risk metrics comparison
 * - Export comparison data
 */

import React, { useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { Download, AlertCircle, CheckCircle2, XCircle } from 'lucide-react';
import { PortfolioInfo, ConsolidatedHoldings } from '../../hooks/useClientLevelData';
import { portfolioApiService, AssetAllocation, RiskMetrics } from '../../services/api/portfolioApiService';
import AllocationPieChart from './molecules/AllocationPieChart';
import Badge from './atoms/Badge';
import TrendIndicator from './atoms/TrendIndicator';

export interface PortfolioComparisonViewProps {
  clientId: string;
  portfolios: PortfolioInfo[];
  selectedPortfolios: string[];
  consolidatedHoldings: ConsolidatedHoldings;
  onSelectPortfolios: (selected: string[]) => void;
  className?: string;
}

const PortfolioComparisonView: React.FC<PortfolioComparisonViewProps> = ({
  clientId,
  portfolios,
  selectedPortfolios,
  consolidatedHoldings,
  onSelectPortfolios,
  className = ''
}) => {
  const [activeSection, setActiveSection] = useState<'allocation' | 'positions' | 'performance' | 'risk'>('allocation');

  // Fetch per-portfolio allocation data
  const allocationQueries = useQueries({
    queries: selectedPortfolios.map((portfolioId) => ({
      queryKey: ['portfolio-allocation', clientId, portfolioId],
      queryFn: () => portfolioApiService.getAllocation(clientId, portfolioId),
      staleTime: 5 * 60 * 1000,
      enabled: !!clientId && !!portfolioId,
    })),
  });

  // Fetch per-portfolio risk data
  const riskQueries = useQueries({
    queries: selectedPortfolios.map((portfolioId) => ({
      queryKey: ['portfolio-risk', clientId, portfolioId],
      queryFn: () => portfolioApiService.getRiskMetrics(clientId, portfolioId),
      staleTime: 5 * 60 * 1000,
      enabled: !!clientId && !!portfolioId,
    })),
  });

  // Build lookup maps: portfolioId → data
  const allocationByPortfolio = new Map<string, AssetAllocation>();
  selectedPortfolios.forEach((id, idx) => {
    const q = allocationQueries[idx];
    if (q?.data?.allocation) allocationByPortfolio.set(id, q.data.allocation);
  });

  const riskByPortfolio = new Map<string, RiskMetrics>();
  selectedPortfolios.forEach((id, idx) => {
    const q = riskQueries[idx];
    if (q?.data?.risk_metrics) riskByPortfolio.set(id, q.data.risk_metrics);
  });

  const formatCurrency = (value: number) => {
    if (value >= 1000000) {
      return `CHF ${(value / 1000000).toFixed(2)}M`;
    } else if (value >= 1000) {
      return `CHF ${(value / 1000).toFixed(0)}K`;
    }
    return `CHF ${value.toLocaleString('de-CH')}`;
  };

  const getSelectedPortfoliosData = () => {
    return portfolios.filter(p => selectedPortfolios.includes(p.id));
  };

  const getOverlappingPositions = () => {
    if (selectedPortfolios.length < 2) return [];

    return consolidatedHoldings.positions.filter(pos => {
      const posPortfolios = pos.portfolios || [];
      return selectedPortfolios.some(spId => posPortfolios.includes(spId)) &&
             posPortfolios.length > 1;
    });
  };

  const getUniquePositions = (portfolioId: string) => {
    return consolidatedHoldings.positions.filter(pos => {
      const posPortfolios = pos.portfolios || [];
      return posPortfolios.includes(portfolioId) && posPortfolios.length === 1;
    });
  };

  const handleExport = () => {
    const comparisonData = {
      date: new Date().toISOString(),
      portfolios: getSelectedPortfoliosData(),
      overlappingPositions: getOverlappingPositions(),
      uniquePositions: selectedPortfolios.map(id => ({
        portfolioId: id,
        positions: getUniquePositions(id)
      }))
    };

    const blob = new Blob([JSON.stringify(comparisonData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `portfolio-comparison-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const selectedData = getSelectedPortfoliosData();
  const showComparison = selectedData.length >= 2;

  // Section tabs
  const sections = [
    { id: 'allocation', label: 'Asset Allocation' },
    { id: 'positions', label: 'Position Overlaps' },
    { id: 'performance', label: 'Performance' },
    { id: 'risk', label: 'Risk Metrics' }
  ] as const;

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Portfolio Selector */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Portfolio Comparison Selector
          </h3>
          {showComparison && (
            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-4 py-2 bg-[#002B45] text-white rounded-lg hover:bg-[#003d5c] transition-colors"
            >
              <Download className="w-4 h-4" />
              Export Comparison
            </button>
          )}
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select portfolios to compare (select 2 or more):
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {portfolios.map((portfolio) => {
              const isSelected = selectedPortfolios.includes(portfolio.id);
              return (
                <label
                  key={portfolio.id}
                  className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    isSelected
                      ? 'border-[#002B45] bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => {
                      if (e.target.checked) {
                        onSelectPortfolios([...selectedPortfolios, portfolio.id]);
                      } else {
                        onSelectPortfolios(selectedPortfolios.filter(id => id !== portfolio.id));
                      }
                    }}
                    className="w-5 h-5 text-[#002B45] border-gray-300 rounded focus:ring-[#002B45]"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{portfolio.name}</div>
                    <div className="text-sm text-gray-500 mt-0.5">
                      {formatCurrency(portfolio.value)} • {portfolio.percentOfClientAuM.toFixed(1)}% of total
                    </div>
                  </div>
                  <div className="text-right">
                    <TrendIndicator
                      value={portfolio.performance}
                      showIcon={true}
                      showValue={true}
                      className="text-sm"
                      suffix=" YTD"
                    />
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        {selectedPortfolios.length > 0 && (
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-700">
              <span className="font-semibold">{selectedPortfolios.length}</span> portfolio{selectedPortfolios.length !== 1 ? 's' : ''} selected
            </p>
            {selectedPortfolios.length > 0 && (
              <button
                onClick={() => onSelectPortfolios([])}
                className="text-sm text-gray-600 hover:text-gray-900 underline"
              >
                Clear selection
              </button>
            )}
          </div>
        )}
      </div>

      {/* Comparison View (shown when 2+ portfolios selected) */}
      {showComparison && (
        <div className="bg-white rounded-lg shadow">
          {/* Section Tabs */}
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                    activeSection === section.id
                      ? 'border-[#002B45] text-[#002B45]'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {section.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Section Content */}
          <div className="p-6">
            {/* Asset Allocation Section */}
            {activeSection === 'allocation' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Asset Allocation Comparison</h3>

                {/* Side-by-side pie charts */}
                <div className={`grid grid-cols-1 ${selectedData.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'} gap-6`}>
                  {selectedData.map((portfolio) => {
                    const allocation = allocationByPortfolio.get(portfolio.id);
                    const chartData = allocation?.by_asset_class
                      ? Object.entries(allocation.by_asset_class)
                          .filter(([, v]) => v > 0)
                          .map(([name, value]) => ({ name, value }))
                      : [];

                    return (
                      <div key={portfolio.id} className="border border-gray-200 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-900 mb-2">{portfolio.name}</h4>
                        {chartData.length > 0 ? (
                          <AllocationPieChart
                            data={chartData}
                            showLegend={true}
                            height={250}
                          />
                        ) : (
                          <div className="h-[250px] flex items-center justify-center text-gray-400 text-sm">
                            Loading allocation...
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Allocation Differences Table */}
                <div className="mt-6">
                  <h4 className="font-semibold text-gray-900 mb-3">Key Differences</h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    {(() => {
                      // Collect all asset classes across selected portfolios
                      const allAssetClasses = new Set<string>();
                      selectedData.forEach((p) => {
                        const alloc = allocationByPortfolio.get(p.id);
                        if (alloc?.by_asset_class) {
                          Object.keys(alloc.by_asset_class).forEach((ac) => allAssetClasses.add(ac));
                        }
                      });
                      const assetClasses = Array.from(allAssetClasses).sort();

                      if (assetClasses.length === 0) {
                        return <p className="text-sm text-gray-500">Loading allocation data...</p>;
                      }

                      return (
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-300">
                              <th className="text-left py-2 text-gray-700">Asset Class</th>
                              {selectedData.map(p => (
                                <th key={p.id} className="text-right py-2 text-gray-700">{p.name}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {assetClasses.map(assetClass => (
                              <tr key={assetClass} className="border-b border-gray-200">
                                <td className="py-2 font-medium text-gray-900">{assetClass}</td>
                                {selectedData.map(p => {
                                  const alloc = allocationByPortfolio.get(p.id);
                                  const value = alloc?.by_asset_class?.[assetClass] ?? 0;
                                  return (
                                    <td key={p.id} className="py-2 text-right font-semibold text-gray-900">
                                      {value > 0 ? `${value.toFixed(1)}%` : '-'}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}

            {/* Position Overlaps Section */}
            {activeSection === 'positions' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Position Overlaps</h3>

                {/* Overlapping Positions */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Holdings in Multiple Portfolios</h4>
                  {getOverlappingPositions().length > 0 ? (
                    <div className="space-y-3">
                      {getOverlappingPositions().map(position => (
                        <div key={position.isin} className="border border-yellow-300 bg-yellow-50 rounded-lg p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <div className="font-semibold text-gray-900">{position.name}</div>
                              <div className="text-xs text-gray-500">ISIN: {position.isin}</div>
                            </div>
                            <div className="text-right">
                              <div className="font-bold text-gray-900">{formatCurrency(position.value)}</div>
                              <div className="text-sm text-yellow-700 font-semibold">
                                {position.percentOfTotal.toFixed(1)}% of total
                              </div>
                            </div>
                          </div>

                          {position.breakdown && (
                            <div className="mt-3 pt-3 border-t border-yellow-200">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {position.breakdown.map((item, idx) => (
                                  <div key={idx} className="flex items-center justify-between bg-white rounded px-3 py-2">
                                    <span className="text-sm text-gray-700">{item.portfolio}</span>
                                    <span className="text-sm font-medium text-gray-900">{formatCurrency(item.value)}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                      <CheckCircle2 className="w-8 h-8 text-green-600 mx-auto mb-2" />
                      <p className="text-sm text-green-700">No overlapping positions detected</p>
                    </div>
                  )}
                </div>

                {/* Unique Positions */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Unique Positions by Portfolio</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedData.map(portfolio => {
                      const uniquePos = getUniquePositions(portfolio.id);
                      return (
                        <div key={portfolio.id} className="border border-gray-200 rounded-lg p-4">
                          <h5 className="font-semibold text-gray-900 mb-2">{portfolio.name}</h5>
                          <p className="text-sm text-gray-600 mb-3">{uniquePos.length} unique position{uniquePos.length !== 1 ? 's' : ''}</p>
                          {uniquePos.length > 0 ? (
                            <div className="space-y-2">
                              {uniquePos.slice(0, 5).map(pos => (
                                <div key={pos.isin} className="flex items-center justify-between text-sm bg-gray-50 rounded px-3 py-2">
                                  <span className="text-gray-700">{pos.name}</span>
                                  <span className="font-medium text-gray-900">{formatCurrency(pos.value)}</span>
                                </div>
                              ))}
                              {uniquePos.length > 5 && (
                                <p className="text-xs text-gray-500 text-center pt-1">
                                  +{uniquePos.length - 5} more
                                </p>
                              )}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500 italic">All positions are shared</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Performance Comparison Section */}
            {activeSection === 'performance' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Performance Comparison</h3>

                {/* Performance Metrics Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Portfolio</th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">YTD</th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">Value</th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">% of Total AuM</th>
                        <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {selectedData.map(portfolio => (
                        <tr key={portfolio.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-900">{portfolio.name}</td>
                          <td className="px-4 py-3 text-right">
                            <TrendIndicator
                              value={portfolio.performance}
                              showIcon={true}
                              showValue={true}
                              className="justify-end"
                            />
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-gray-900">
                            {formatCurrency(portfolio.value)}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-700">
                            {portfolio.percentOfClientAuM.toFixed(1)}%
                          </td>
                          <td className="px-4 py-3 text-center">
                            {portfolio.performance == null ? (
                              <Badge variant="default" label="N/A" />
                            ) : portfolio.performance >= 10 ? (
                              <Badge variant="success" label="Excellent" />
                            ) : portfolio.performance >= 5 ? (
                              <Badge variant="default" label="Good" />
                            ) : portfolio.performance >= 0 ? (
                              <Badge variant="warning" label="Fair" />
                            ) : (
                              <Badge variant="error" label="Poor" />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Performance Insights */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-2">Performance Insights</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    {(() => {
                      const withPerf = selectedData.filter(p => p.performance != null);
                      const best = withPerf.length > 0 ? withPerf.reduce((b, p) => (p.performance ?? 0) > (b.performance ?? 0) ? p : b) : null;
                      const avg = withPerf.length > 0 ? withPerf.reduce((sum, p) => sum + (p.performance ?? 0), 0) / withPerf.length : null;
                      return <>
                        {best && <li>• Best performer: <strong>{best.name}</strong> ({best.performance!.toFixed(1)}% YTD)</li>}
                        <li>• Largest portfolio: <strong>{selectedData.reduce((largest, p) => p.value > largest.value ? p : largest).name}</strong> ({formatCurrency(selectedData.reduce((largest, p) => p.value > largest.value ? p : largest).value)})</li>
                        {avg != null ? <li>• Average YTD return: <strong>{avg.toFixed(1)}%</strong></li> : <li>• Average YTD return: <strong>N/A</strong></li>}
                      </>;
                    })()}
                  </ul>
                </div>
              </div>
            )}

            {/* Risk Metrics Comparison Section */}
            {activeSection === 'risk' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Risk Metrics Comparison</h3>

                {/* Risk Metrics Cards */}
                <div className={`grid grid-cols-1 ${selectedData.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'} gap-4`}>
                  {selectedData.map(portfolio => {
                    const risk = riskByPortfolio.get(portfolio.id);

                    return (
                      <div key={portfolio.id} className="border border-gray-200 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-900 mb-4">{portfolio.name}</h4>
                        {risk ? (
                          <div className="space-y-3">
                            <div>
                              <div className="text-xs text-gray-500 mb-1">Volatility</div>
                              <div className="text-2xl font-bold text-gray-900">{risk.volatility != null ? `${risk.volatility.toFixed(1)}%` : 'N/A'}</div>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 mb-1">Sharpe Ratio</div>
                              <div className="text-2xl font-bold text-gray-900">{risk.sharpe_ratio != null ? risk.sharpe_ratio.toFixed(2) : 'N/A'}</div>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 mb-1">Beta</div>
                              <div className="text-2xl font-bold text-gray-900">{risk.beta != null ? risk.beta.toFixed(2) : 'N/A'}</div>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 mb-1">VaR (1 day, 95%)</div>
                              <div className="text-2xl font-bold text-gray-900">{risk.var_1day != null ? formatCurrency(risk.var_1day) : 'N/A'}</div>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 mb-1">Concentration Risk</div>
                              <div className="text-2xl font-bold text-gray-900">{risk.concentration_risk.toFixed(1)}%</div>
                            </div>
                            {risk.volatility != null && (
                              <div className="pt-2 border-t border-gray-200">
                                <Badge
                                  variant={risk.volatility < 10 ? 'success' : risk.volatility < 20 ? 'warning' : 'error'}
                                  label={`Risk: ${risk.volatility < 10 ? 'Low' : risk.volatility < 20 ? 'Medium' : 'High'}`}
                                />
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-sm text-gray-400 py-8 text-center">Loading risk metrics...</div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Risk Comparison Insights */}
                {(() => {
                  // Only compare portfolios with a real (non-null) volatility — TASK-582.
                  const riskEntries = selectedData
                    .map(p => ({ name: p.name, vol: riskByPortfolio.get(p.id)?.volatility }))
                    .filter((e): e is { name: string; vol: number } => e.vol != null);

                  if (riskEntries.length < 2) return null;

                  const highestVol = riskEntries.reduce((a, b) => (b.vol > a.vol ? b : a));
                  const lowestVol = riskEntries.reduce((a, b) => (b.vol < a.vol ? b : a));

                  return (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <h4 className="font-semibold text-yellow-900 mb-2">Risk Analysis</h4>
                      <ul className="text-sm text-yellow-800 space-y-1">
                        <li>• Highest volatility: <strong>{highestVol.name}</strong> ({highestVol.vol.toFixed(1)}%)</li>
                        <li>• Lowest volatility: <strong>{lowestVol.name}</strong> ({lowestVol.vol.toFixed(1)}%)</li>
                        <li>• Volatility spread: <strong>{(highestVol.vol - lowestVol.vol).toFixed(1)}%</strong></li>
                      </ul>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Message when less than 2 portfolios selected */}
      {!showComparison && selectedPortfolios.length === 1 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
          <AlertCircle className="w-12 h-12 text-blue-600 mx-auto mb-3" />
          <h4 className="text-lg font-semibold text-blue-900 mb-2">Select Another Portfolio</h4>
          <p className="text-sm text-blue-700">
            You've selected 1 portfolio. Please select at least one more portfolio to enable comparison.
          </p>
        </div>
      )}

      {!showComparison && selectedPortfolios.length === 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
          <XCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <h4 className="text-lg font-semibold text-gray-700 mb-2">No Portfolios Selected</h4>
          <p className="text-sm text-gray-600">
            Select 2 or more portfolios above to start comparing.
          </p>
        </div>
      )}
    </div>
  );
};

export default PortfolioComparisonView;
